(function () {
  function parseInstanceIdFromBundleId(bundleId, bundleGroup) {
    if (!bundleId) return null;
    if (bundleGroup && bundleId.startsWith(`${bundleGroup}-`)) {
      return bundleId.slice(bundleGroup.length + 1);
    }
    const idx = bundleId.indexOf('-');
    if (idx === -1) return null;
    return bundleId.slice(idx + 1);
  }

  function isBundleParentLine(line) {
    const props = line.properties || {};
    const bundleId = props._BundleId;
    if (!bundleId || props._BundleGift === '1') return false;
    if (props._BundleTitle || props._BundleParentHandle || props._BundleParentImage) return true;
    return Array.isArray(line.item_components) && line.item_components.length > 0;
  }

  function getRelatedBundleLineKeys(cartItems, removedLine) {
    if (!isBundleParentLine(removedLine)) return null;

    const props = removedLine.properties || {};
    const bundleId = props._BundleId;
    const instanceId =
      props._BundleInstance ||
      parseInstanceIdFromBundleId(bundleId, props._BundleGroup);

    if (!bundleId && !instanceId) return null;

    const keys = [];
    cartItems.forEach((item) => {
      const itemProps = item.properties || {};
      if (bundleId && itemProps._BundleId === bundleId) {
        keys.push(item.key);
        return;
      }
      if (instanceId && itemProps._BundleInstance === instanceId) {
        keys.push(item.key);
      }
    });

    return keys.length ? keys : null;
  }

  function buildUpdates(keys, quantity) {
    const updates = {};
    keys.forEach((key) => {
      updates[key] = quantity;
    });
    return updates;
  }

  async function fetchCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  async function updateCartLines(updates, sectionsToBundle) {
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        sections: sectionsToBundle
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to update cart');
    }

    return response.json();
  }

  function getSectionsToBundle() {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );
    return sectionsToBundle;
  }

  function removeLineItemsFromDom(keys) {
    keys.forEach((key) => {
      const input = document.querySelector(`input[data-line-key="${key}"]`);
      const lineItem = input?.closest('line-item');
      if (lineItem) lineItem.remove();
    });
  }

  function dispatchCartEvents(lineKey, lineItemEl, cartContent, targetQuantity) {
    const lineItemAfterChange = cartContent.items.filter((item) => item.key === lineKey);
    lineItemEl?.dispatchEvent(
      new CustomEvent('line-item:change', {
        bubbles: true,
        detail: {
          quantity:
            lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0].quantity,
          cart: cartContent
        }
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'line-item:change',
          cart: cartContent
        }
      })
    );
  }

  async function changeSingleLineQuantity(lineKey, targetQuantity, lineItemEl) {
    lineItemEl?.dispatchEvent(
      new CustomEvent('line-item:will-change', {
        bubbles: true,
        detail: { targetQuantity }
      })
    );

    const sectionsToBundle = getSectionsToBundle();
    const cartContent = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: targetQuantity,
        sections: sectionsToBundle
      })
    }).then((response) => {
      if (!response.ok) throw new Error('Failed to change line item');
      return response.json();
    });

    dispatchCartEvents(lineKey, lineItemEl, cartContent, targetQuantity);
  }

  async function handleLineQuantityChange(lineKey, targetQuantity, lineItemEl) {
    if (targetQuantity !== 0) {
      await changeSingleLineQuantity(lineKey, targetQuantity, lineItemEl);
      return;
    }

    const cart = await fetchCart();
    const removedLine = cart.items.find((item) => item.key === lineKey);
    if (!removedLine) return;

    const relatedKeys = getRelatedBundleLineKeys(cart.items, removedLine);
    const keysToRemove = relatedKeys || [lineKey];

    lineItemEl?.dispatchEvent(
      new CustomEvent('line-item:will-change', {
        bubbles: true,
        detail: { targetQuantity: 0 }
      })
    );

    const sectionsToBundle = getSectionsToBundle();
    const cartContent = await updateCartLines(buildUpdates(keysToRemove, 0), sectionsToBundle);

    if (keysToRemove.length > 1) {
      removeLineItemsFromDom(keysToRemove);
    }

    dispatchCartEvents(lineKey, lineItemEl, cartContent, 0);
  }

  function initLineItemQuantityInterceptor() {
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest('line-item-quantity a[href*="/cart/change"]');
        if (!link) return;

        const url = new URL(link.href);
        const quantity = parseInt(url.searchParams.get('quantity'), 10);
        if (quantity !== 0) return;

        const isCartPage = window.themeVariables?.settings?.pageType === 'cart';
        if (isCartPage) {
          event.preventDefault();
          event.stopImmediatePropagation();
          const lineKey = url.searchParams.get('id');
          handleLineQuantityChange(lineKey, 0, null).then(() => {
            window.location.reload();
          }).catch((error) => {
            console.error('Bundle cart removal failed:', error);
          });
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const lineKey = url.searchParams.get('id');
        const lineItemEl = link.closest('line-item');
        handleLineQuantityChange(lineKey, 0, lineItemEl).catch((error) => {
          console.error('Bundle cart removal failed:', error);
        });
      },
      true
    );

    document.addEventListener('change', (event) => {
      if (!event.target.hasAttribute('data-line-key')) return;
      if (event.target.closest('line-item-quantity') == null) return;

      const targetQuantity = parseInt(event.target.value, 10);
      if (targetQuantity !== 0) return;

      const isCartPage = window.themeVariables?.settings?.pageType === 'cart';
      if (isCartPage) {
        event.stopImmediatePropagation();
        const lineKey = event.target.getAttribute('data-line-key');
        handleLineQuantityChange(lineKey, 0, null).then(() => {
          window.location.reload();
        }).catch((error) => {
          console.error('Bundle cart removal failed:', error);
        });
        return;
      }

      event.stopImmediatePropagation();

      const lineKey = event.target.getAttribute('data-line-key');
      const lineItemEl = event.target.closest('line-item');
      handleLineQuantityChange(lineKey, 0, lineItemEl).catch((error) => {
        console.error('Bundle cart removal failed:', error);
      });
    }, true);
  }

  function parseBundlePricingAttribute(value) {
    const map = {};
    String(value || '')
      .split('|')
      .filter(Boolean)
      .forEach((entry) => {
        const [key, compare, final] = entry.split(':');
        if (!key) return;
        map[key] = {
          compare: parseInt(compare, 10) || 0,
          final: parseInt(final, 10) || 0
        };
      });
    return map;
  }

  function serializeBundlePricingAttribute(map) {
    return Object.entries(map || {})
      .filter(([, pricing]) => pricing && (pricing.compare > 0 || pricing.final > 0))
      .map(([key, pricing]) => `${key}:${pricing.compare}:${pricing.final}`)
      .join('|');
  }

  function lookupBundlePricing(map, bundleKey, bundleInstance) {
    if (!map) return null;
    if (bundleKey && map[bundleKey]) return map[bundleKey];
    if (bundleInstance && map[bundleInstance]) return map[bundleInstance];
    return null;
  }

  function formatBundleMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return `${(cents / 100).toFixed(2).replace('.', ',')}\u00a0€`;
  }

  function readLineProperty(props, key) {
    if (!props) return '';
    if (props[key] != null && props[key] !== '') return props[key];
    const match = Object.entries(props).find(([name]) => String(name).trim() === key);
    return match ? match[1] : '';
  }

  function resolveCartLineBundleKeys(cartLine) {
    const props = cartLine?.properties || {};
    let bundleId = readLineProperty(props, '_BundleId') || readLineProperty(props, '_DiscountId') || '';
    let instanceId = readLineProperty(props, '_BundleInstance') || '';

    if (!bundleId && Array.isArray(cartLine?.item_components)) {
      cartLine.item_components.forEach((component) => {
        if (bundleId) return;
        const componentProps = component?.properties || {};
        bundleId = componentProps._BundleId || componentProps._DiscountId || '';
        instanceId = componentProps._BundleInstance || instanceId;
      });
    }

    if (!instanceId && bundleId) {
      instanceId = parseInstanceIdFromBundleId(bundleId, props._BundleGroup);
    }

    return { bundleId, instanceId };
  }

  function getLineItemPricing(cartLine, map) {
    const props = cartLine?.properties || {};
    if (readLineProperty(props, '_BundleExternalAddon') === '1') {
      return {
        compare: 0,
        final: parseInt(cartLine?.final_line_price, 10) || 0,
        bundleId: '',
        instanceId: ''
      };
    }

    const { bundleId, instanceId } = resolveCartLineBundleKeys(cartLine);
    const fromAttr = lookupBundlePricing(map, bundleId, instanceId);
    const fromPropsCompare =
      parseInt(
        readLineProperty(props, '_BundleListCompareCents') ||
          readLineProperty(props, '_BundleCompareAt') ||
          readLineProperty(props, '_BundleCustomCompareCents'),
        10
      ) || 0;
    const fromPropsFinal =
      parseInt(
        readLineProperty(props, '_BundleListFinalCents') || readLineProperty(props, '_BundleFinal'),
        10
      ) || 0;
    const compare = Math.max(fromAttr?.compare || 0, fromPropsCompare);
    const final =
      (fromAttr?.final || 0) > 0
        ? fromAttr.final
        : fromPropsFinal > 0
          ? fromPropsFinal
          : parseInt(cartLine?.final_line_price, 10) || 0;
    return { compare, final, bundleId, instanceId };
  }

  function findLineItemElement(cartLine, bundleId, instanceId) {
    const byKey = document
      .querySelector(`line-item input[data-line-key="${cartLine?.key}"]`)
      ?.closest('line-item');
    if (byKey) return byKey;
    if (instanceId) {
      const byInstance = [...document.querySelectorAll(`[data-bundle-pricing-instance="${instanceId}"]`)]
        .find((el) => el.getAttribute('data-bundle-external-addon') !== 'true')
        ?.closest('line-item');
      if (byInstance) return byInstance;
    }
    if (bundleId) {
      return [...document.querySelectorAll(`[data-bundle-pricing-key="${bundleId}"]`)]
        .find((el) => el.getAttribute('data-bundle-external-addon') !== 'true')
        ?.closest('line-item');
    }
    return null;
  }

  function updateLineItemPriceElement(priceEl, pricing) {
    if (!priceEl || !pricing || pricing.compare <= pricing.final) return;

    priceEl.setAttribute('data-compare-cents', String(pricing.compare));
    priceEl.setAttribute('data-final-cents', String(pricing.final));

    const priceList = priceEl.querySelector('price-list');
    if (!priceList) return;

    const saleEl = priceList.querySelector('sale-price');
    if (!saleEl) return;

    let compareEl = priceList.querySelector('compare-at-price');
    const formattedFinal = formatBundleMoney(pricing.final);
    const formattedCompare = formatBundleMoney(pricing.compare);
    const saleSrOnly =
      saleEl.querySelector('.sr-only')?.outerHTML || '<span class="sr-only">Sale price</span>';
    const compareSrOnly =
      compareEl?.querySelector('.sr-only')?.outerHTML ||
      '<span class="sr-only">Regular price</span>';

    saleEl.className = 'text-on-sale';
    saleEl.innerHTML = `${saleSrOnly}${formattedFinal}`;

    if (!compareEl) {
      compareEl = document.createElement('compare-at-price');
      compareEl.className = 'text-subdued line-through';
      priceList.appendChild(compareEl);
    }

    compareEl.hidden = false;
    compareEl.innerHTML = `${compareSrOnly}${formattedCompare}`;
  }

  function getRemainingLineKeys(cart) {
    return new Set((cart?.items || []).map((item) => item.key).filter(Boolean));
  }

  function getCartDrawerSectionHtml(cart) {
    if (!cart?.sections) return null;
    const drawer = document.getElementById('cart-drawer');
    const sectionId = drawer?.getAttribute('data-section-id');
    if (sectionId && cart.sections[sectionId]) return cart.sections[sectionId];

    const keys = Object.keys(cart.sections);
    for (let i = 0; i < keys.length; i += 1) {
      const html = cart.sections[keys[i]];
      if (typeof html === 'string' && html.includes('data-cart-total-prices')) return html;
    }
    return null;
  }

  function applyFooterFromSectionHtml(cart) {
    const html = getCartDrawerSectionHtml(cart);
    const drawer = document.getElementById('cart-drawer');
    if (!html || !drawer) return false;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const srcTotalPrices = doc.querySelector('[data-cart-total-prices]');
    if (!srcTotalPrices) return false;

    const destTotalPrices = drawer.querySelector('[data-cart-total-prices]');
    const destTotalAmount = drawer.querySelector('[data-cart-total-amount]');
    const destRow = drawer.querySelector('[data-cart-savings-row]');
    const destAmount = drawer.querySelector('[data-cart-savings-amount]');
    const destCompare = drawer.querySelector('[data-cart-total-compare]');
    const srcTotalAmount = doc.querySelector('[data-cart-total-amount]');
    const srcRow = doc.querySelector('[data-cart-savings-row]');
    const srcAmount = doc.querySelector('[data-cart-savings-amount]');
    const srcCompare = doc.querySelector('[data-cart-total-compare]');

    if (destTotalPrices) {
      destTotalPrices.setAttribute(
        'data-cart-total-cents',
        srcTotalPrices.getAttribute('data-cart-total-cents') || ''
      );
    }
    if (destTotalAmount && srcTotalAmount) {
      destTotalAmount.className = srcTotalAmount.className;
      destTotalAmount.innerHTML = srcTotalAmount.innerHTML;
    }
    if (destRow && srcRow) {
      destRow.hidden = srcRow.hidden;
    }
    if (destAmount && srcAmount) {
      destAmount.innerHTML = srcAmount.innerHTML;
    }
    if (destCompare && srcCompare) {
      destCompare.hidden = srcCompare.hidden;
      destCompare.innerHTML = srcCompare.innerHTML;
    } else if (destCompare && !srcCompare) {
      destCompare.hidden = true;
    }
    return true;
  }

  function updateCartDrawerSavings(cart) {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    const row = drawer.querySelector('[data-cart-savings-row]');
    const amountEl = drawer.querySelector('[data-cart-savings-amount]');
    const totalPricesEl = drawer.querySelector('[data-cart-total-prices]');
    const totalAmountEl = drawer.querySelector('[data-cart-total-amount]');
    const compareEl = drawer.querySelector('[data-cart-total-compare]');
    const remainingKeys = getRemainingLineKeys(cart);

    let savings = 0;
    drawer.querySelectorAll('.cart-drawer__line-items .bk-media-line-item-prices').forEach((el) => {
      const lineItem = el.closest('line-item');
      const key = lineItem?.querySelector('input[data-line-key]')?.getAttribute('data-line-key');
      if (remainingKeys.size > 0 && (!key || !remainingKeys.has(key))) return;

      const compare = parseInt(el.getAttribute('data-compare-cents'), 10) || 0;
      const final = parseInt(el.getAttribute('data-final-cents'), 10) || 0;
      if (compare > final) savings += compare - final;
    });

    if (row && amountEl) {
      if (savings > 0) {
        row.hidden = false;
        amountEl.textContent = formatBundleMoney(savings);
      } else {
        row.hidden = true;
      }
    }

    const totalCents =
      cart?.total_price != null
        ? parseInt(cart.total_price, 10) || 0
        : parseInt(totalPricesEl?.getAttribute('data-cart-total-cents'), 10) || 0;

    if (totalPricesEl && totalCents >= 0) {
      totalPricesEl.setAttribute('data-cart-total-cents', String(totalCents));
    }

    if (compareEl) {
      if (savings > 0 && totalCents > 0) {
        const compareSrOnly =
          compareEl.querySelector('.sr-only')?.outerHTML ||
          '<span class="sr-only">Regular price</span>';
        compareEl.hidden = false;
        compareEl.innerHTML = `${compareSrOnly}${formatBundleMoney(totalCents + savings)}`;
        if (totalAmountEl) totalAmountEl.classList.add('text-on-sale');
      } else {
        compareEl.hidden = true;
        if (totalAmountEl) totalAmountEl.classList.remove('text-on-sale');
      }
    }
  }

  function applyBundlePricesToLineItems(cart) {
    const map = parseBundlePricingAttribute(cart?.attributes?._BundlePricing);
    let overlayChanged = false;
    if (!Array.isArray(cart?.items)) return overlayChanged;

    cart.items.forEach((cartLine) => {
      if (readLineProperty(cartLine?.properties, '_BundleExternalAddon') === '1') return;
      const pricing = getLineItemPricing(cartLine, map);
      if (!pricing.compare || pricing.compare <= pricing.final) return;

      const lineItemEl = findLineItemElement(cartLine, pricing.bundleId, pricing.instanceId);
      if (!lineItemEl) return;

      const priceEl = lineItemEl.querySelector('.bk-media-line-item-prices');
      if (!priceEl) return;

      const prevCompare = parseInt(priceEl.getAttribute('data-compare-cents'), 10) || 0;
      const prevFinal = parseInt(priceEl.getAttribute('data-final-cents'), 10) || 0;

      if (pricing.bundleId) {
        priceEl.setAttribute('data-bundle-pricing-key', pricing.bundleId);
      }
      if (pricing.instanceId) {
        priceEl.setAttribute('data-bundle-pricing-instance', pricing.instanceId);
      }

      updateLineItemPriceElement(priceEl, pricing);

      const nextCompare = parseInt(priceEl.getAttribute('data-compare-cents'), 10) || 0;
      const nextFinal = parseInt(priceEl.getAttribute('data-final-cents'), 10) || 0;
      if (nextCompare !== prevCompare || nextFinal !== prevFinal) overlayChanged = true;
    });

    return overlayChanged;
  }

  let applyingPricing = false;

  function applyCartDrawerBundlePricing(cart) {
    if (applyingPricing) return;
    applyingPricing = true;
    try {
      const overlayChanged = applyBundlePricesToLineItems(cart);
      if (overlayChanged || !applyFooterFromSectionHtml(cart)) {
        updateCartDrawerSavings(cart);
      }
      observeCartDrawerLineItems(cart);
    } finally {
      applyingPricing = false;
    }
  }

  async function persistBundleCartPricing({ bundleId, instanceId, compareCents, finalCents, sections }) {
    const compare = parseInt(compareCents, 10) || 0;
    const final = parseInt(finalCents, 10) || 0;
    if (compare <= final || final <= 0) return null;

    const cart = await fetchCart();
    const map = parseBundlePricingAttribute(cart.attributes?._BundlePricing);
    const entry = { compare, final };

    if (bundleId) map[bundleId] = entry;
    if (instanceId) map[instanceId] = entry;

    const payload = {
      attributes: {
        ...(cart.attributes || {}),
        _BundlePricing: serializeBundlePricingAttribute(map)
      }
    };
    if (Array.isArray(sections) && sections.length) {
      payload.sections = sections.join(',');
    }

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('Failed to persist bundle pricing');
    }

    return response.json();
  }

  let pricingApplyTimers = [];
  let lastPricingCart = null;
  let lineItemsObserver = null;

  function observeCartDrawerLineItems(cart) {
    lastPricingCart = cart || lastPricingCart;
    const list = document.querySelector('#cart-drawer .cart-drawer__line-items');
    if (!list) return;
    if (lineItemsObserver && lineItemsObserver.target === list) return;

    if (lineItemsObserver) {
      lineItemsObserver.disconnect();
      lineItemsObserver = null;
    }

    const observer = new MutationObserver(() => {
      if (!lastPricingCart) return;
      applyCartDrawerBundlePricing(lastPricingCart);
    });
    observer.observe(list, { childList: true });
    observer.target = list;
    lineItemsObserver = observer;
  }

  function scheduleCartDrawerPricingApply(cart) {
    lastPricingCart = cart;
    pricingApplyTimers.forEach((id) => clearTimeout(id));
    pricingApplyTimers = [];

    const run = () => applyCartDrawerBundlePricing(cart);
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    [0, 50, 150, 250, 400, 1300].forEach((delay) => {
      pricingApplyTimers.push(setTimeout(run, delay));
    });
  }

  const ORDER_TAGS_ATTRIBUTE = '_order_tags';
  let orderTagsQueue = Promise.resolve();

  function collectOrderTags(cart) {
    const seen = new Set();
    const tags = [];
    (cart?.items || []).forEach((item) => {
      const raw = readLineProperty(item?.properties, '_OrderTags');
      String(raw || '')
        .split(',')
        .forEach((part) => {
          const tag = part.trim();
          if (!tag) return;
          const key = tag.toLowerCase();
          if (seen.has(key)) return;
          seen.add(key);
          tags.push(tag);
        });
    });
    return tags.join(', ');
  }

  async function syncOrderTagsAttribute(cart) {
    const source = cart?.items ? cart : await fetchCart();
    const desired = collectOrderTags(source);
    const current = String(source.attributes?.[ORDER_TAGS_ATTRIBUTE] || '').trim();
    if (desired === current) return source;

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          [ORDER_TAGS_ATTRIBUTE]: desired
        }
      })
    });

    if (!response.ok) throw new Error('Failed to sync order tags');
    return response.json();
  }

  function queueOrderTagsSync(cart) {
    orderTagsQueue = orderTagsQueue
      .then(() => syncOrderTagsAttribute(cart))
      .catch(() => {});
    return orderTagsQueue;
  }

  window.BkMediaOrderTags = {
    sync: queueOrderTagsSync
  };

  function initBundleCartPricing() {
    document.addEventListener('cart:change', (event) => {
      const cart = event.detail?.cart;
      if (!cart) return;
      scheduleCartDrawerPricingApply(cart);
      queueOrderTagsSync(null);
    });

    document.addEventListener('variant:add', (event) => {
      const cart = event.detail?.cart;
      if (!cart) return;
      scheduleCartDrawerPricingApply(cart);
    });

    document.addEventListener('line-item:change', (event) => {
      const cart = event.detail?.cart;
      if (!cart) return;
      lastPricingCart = cart;
      applyCartDrawerBundlePricing(cart);
    });

    fetchCart()
      .then((cart) => {
        scheduleCartDrawerPricingApply(cart);
        queueOrderTagsSync(cart);
      })
      .catch(() => {});
  }

  window.BkMediaBundleCartPricing = {
    parseAttribute: parseBundlePricingAttribute,
    serializeAttribute: serializeBundlePricingAttribute,
    persist: persistBundleCartPricing,
    applyCartDrawerPrices: applyCartDrawerBundlePricing
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLineItemQuantityInterceptor, { once: true });
    document.addEventListener('DOMContentLoaded', initBundleCartPricing, { once: true });
  } else {
    initLineItemQuantityInterceptor();
    initBundleCartPricing();
  }
})();
